import { useRecoilCallback, useRecoilValue } from 'recoil';
import { InputGroup as BootstrapInputGroup } from 'react-bootstrap';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import log from 'common/ScalyrLogger';
import { UrlParams } from 'common/types';
import { TOKEN_TYPE } from 'common/util/filter/types';
import { buildParamsWithDeconstructedFilter } from 'common/util/filter/filter';
import { useSetSearchParams } from 'hooks/useSetSearchParams';

import Button, { ButtonVariant } from 'components/common/Button/Button';
import Toolbar from 'components/common/Toolbar/Toolbar';
import TimeRangeDropdown from 'components/common/TimeRangeDropdown/TimeRangeDropdown';
import CrossTeamSearch from 'components/search/CrossTeamSearch/CrossTeamSearch';
import { TimeRangeDropdownValidationStatus, useTimeRangeDropdownState } from 'components/common/TimeRangeDropdown/TimeRangeDropdown.hooks';
import FilterInput from 'components/search/FilterInput';
import { isSearchPageResultsPendingSelector } from 'components/search/SearchLogsPage/SearchStateManager.state';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import { CROSS_TEAM_SEARCH_ROUTES, draftSearchParamState, routeState } from 'components/common/Managers/RouteStateManager.state';
import { viewState } from 'components/common/Managers/ViewStateManager.state';
import { accountDataState } from 'components/account/account.state';
import { setMark } from 'common/PerformanceLogger';

import ViewSelector from './ViewSelector/ViewSelector';

import './SearchToolbar.scss';

type SearchToolbarProps = {
  excludeTypes?:  TOKEN_TYPE[];
  shouldRemoveQueryTermsFromFilter?: boolean;
};

/**
 * SearchToolbar is a toolbar that can be used to perform a search.
 *
 * @prop excludeTypes     token types to exclude from matching
 */
function SearchToolbar({
  excludeTypes = [],
  shouldRemoveQueryTermsFromFilter = true,
}: SearchToolbarProps): JSX.Element {

  const route = useRecoilValue(routeState);
  const setSearchParams = useSetSearchParams();
  const { handleInputValidation: handleTimeRangeDropdownInputValidation } = useTimeRangeDropdownState();
  const isSearchPageResultsPending = useRecoilValue(isSearchPageResultsPendingSelector);

  // Run search by copying value of filter from Apollo (which tracks the search box) to URL
  const runSearch = useRecoilCallback(({ snapshot }) => (): void => {
    if (isSearchPageResultsPending) { return; }

    const { status } = handleTimeRangeDropdownInputValidation();
    if (status === TimeRangeDropdownValidationStatus.ERROR) {
      log.debug("/events page: Search won't be issued, invalid input in TimeRangeDropdown");
      return;
    }
    const draftParams = snapshot.getLoadable(draftSearchParamState).contents as UrlParams;
    const view = snapshot.getLoadable(viewState).getValue();
    const adjustedParams = shouldRemoveQueryTermsFromFilter ? buildParamsWithDeconstructedFilter(draftParams, view) : draftParams;
    setMark('runSearch', { adjustedParams });
    setSearchParams(adjustedParams, true);
  });

  const account = useRecoilValue(accountDataState);
  const isCrossTeamSearchEnabled = CROSS_TEAM_SEARCH_ROUTES.includes(route);

  const items = [
    <BootstrapInputGroup className="FilterInputGroup">
      {isCrossTeamSearchEnabled && (
        <BootstrapInputGroup.Prepend>
          <CrossTeamSearch />
        </BootstrapInputGroup.Prepend>
      )}
      {account.isSingularityXDRUser && (
        <BootstrapInputGroup.Prepend>
          <ViewSelector crossTeamSearchEnabled={isCrossTeamSearchEnabled} />
        </BootstrapInputGroup.Prepend>
      )}
      <FilterInput onEnter={runSearch} crossTeamSearchEnabled={isCrossTeamSearchEnabled} excludeTypes={excludeTypes} />
    </BootstrapInputGroup>,
    <TimeRangeDropdown className="SearchToolbar__Button dropdown" />,
    <Button
      className="SearchToolbar__Button"
      variant={ButtonVariant.PRIMARY}
      onClick={runSearch}
      testId={ELEMENT.searchButton}
    >
      {isSearchPageResultsPending ? <LoadingPlaceholder offsetTop={0} spinnerHeight={21} /> : 'Search'}
    </Button>,
  ];

  return (
    <Toolbar
      className="SearchToolbar"
      items={items}
      fillItemIndex={0}
      alignment={K.ALIGNMENT.START}
      testId={ELEMENT.searchToolbar}
    />
  );
}

export default SearchToolbar;
